////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.1                                                            //
//  Summary: Accessible combobox select (downshift) — project/news filters   //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { useSelect } from 'downshift';
import { ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  id: string;
  label: string;
  options: SelectOption[];
  value: SelectOption | null;
  onChange: (value: SelectOption | null) => void;
  placeholder?: string;
  /** Renders a genuinely disabled, non-interactive control — used by the Projects
   * filter panel while `content.projects` is empty (1.0.2.md §5.2: "filters render
   * disabled"). A native `disabled` button attribute is sufficient on its own (the
   * browser never dispatches the click downshift listens for), so this stays a thin
   * pass-through rather than a second control implementation. */
  disabled?: boolean;
}

// downshift's useSelect owns the full keyboard/ARIA combobox contract (roving
// focus, aria-activedescendant, Home/End/typeahead) — reimplementing that on a
// bare <div> is exactly the kind of broken-keyboard-access defect the house rules
// call out, and it is a small, already-in-the-tree dependency rather than a new one.
export function Select ({ id, label, options, value, onChange, placeholder = 'Select…', disabled = false }: SelectProps) {
  const { isOpen, getToggleButtonProps, getMenuProps, getItemProps, highlightedIndex } = useSelect({
    id,
    items: options,
    itemToString: (item) => item?.label ?? '',
    selectedItem: value,
    onSelectedItemChange: ({ selectedItem }) => onChange(selectedItem ?? null),
  });

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        {...getToggleButtonProps({ 'aria-label': label })}
        className="flex w-full items-center justify-between rounded border border-line px-3 py-2 text-left text-ink disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span>{value?.label ?? placeholder}</span>
        <ChevronDown aria-hidden="true" className={isOpen ? 'rotate-180' : ''} />
      </button>
      <ul
        {...getMenuProps()}
        className={`absolute z-10 mt-1 w-full rounded border border-line bg-paper shadow-lg ${isOpen ? '' : 'hidden'}`}
      >
        {isOpen &&
          options.map((option, index) => (
            <li
              key={option.value}
              {...getItemProps({ item: option, index })}
              className={`cursor-pointer px-3 py-2 ${highlightedIndex === index ? 'bg-line/40' : ''}`}
            >
              {option.label}
            </li>
          ))}
      </ul>
    </div>
  );
}
